import moment from 'moment';

import RawSequenceData from '../models/raw-sequence-data';
import {parseFasta} from './fasta';
import {guessFileType, readFile, dumpTSVFile, parseCSVFile} from './file-utils';
import {NCBI_APIKEY} from '../constants';
import {startCase} from 'lodash';

let CSV_HEADERS = Object.keys(RawSequenceData.propTypes);
CSV_HEADERS = CSV_HEADERS.filter((header) => {
  return (header != 'uuid' && header != 'mutationMap');
});
const MUTS_CSV_HEADERS = Array.from(CSV_HEADERS);
MUTS_CSV_HEADERS.splice(MUTS_CSV_HEADERS.indexOf('sequence'), 1);
MUTS_CSV_HEADERS.splice(MUTS_CSV_HEADERS.indexOf('accession'), 1);
MUTS_CSV_HEADERS.push('gene');
MUTS_CSV_HEADERS.push('mutations');


function popFromQualifier(qualifier, name, targetName) {
  const qualifierName = qualifier.querySelector('GBQualifier_name');
  const result = {};
  if (qualifierName.textContent === name) {
    let text = qualifier
      .querySelector('GBQualifier_value').textContent;
    if (typeof(text) === 'string') {
      // \r or \n are not allowed
      text = text.replace(/[\r\n]/g, ' ');
    }
    result[targetName] = text;
  }
  return result;
}


function popIsolationSourceFromMolType(qualifier) {
  const qualifierName = qualifier.querySelector('GBQualifier_name');
  const result = {};
  if (qualifierName.textContent === 'mol_type') {
    const value = qualifier
      .querySelector('GBQualifier_value').textContent;
    if (/genomic DNA/i.test(value)) {
      result.source = value;
    }
    else if (/genomic RNA/i.test(value)) {
      result.source = 'Plasma';
    }
    else {
      result.source = `Unknown ${value}`;
    }
  }
  return result;
}


function popIsolationSource(qualifier) {
  const qualifierName = qualifier.querySelector('GBQualifier_name');
  const result = {};
  if (qualifierName.textContent === 'isolation_source') {
    const value = qualifier
      .querySelector('GBQualifier_value').textContent;
    if (/\bPBMC\b/i.test(value)) {
      result.source = 'PBMC';
    }
    else if (/\bPlasma\b/i.test(value)) {
      result.source = 'Plasma';
    }
    else if (/\bPeripheral blood mononuclear cells\b/i.test(value)) {
      result.source = 'PBMC';
    }
    else if (value){
      result.source = startCase(value);
    }
  }
  return result;
}


function popIsolateDateFromQualifiers(qualifier, names = ['note', 'isolation_source']) {
  const qualifierName = qualifier.querySelector('GBQualifier_name');
  const result = {};
  for (const name of names) {
    if (qualifierName.textContent === name) {
      const value = qualifier
        .querySelector('GBQualifier_value').textContent;
      let validDate;
      let partial = [];
      for (const v of value.split(/\s+/g)) {
        if (!v) {
          continue;
        }
        partial.push(v);
        if (partial.length > 3) {
          partial = partial.splice(1);
        }
        const joined = partial.join(' ');
        if (moment(joined).isValid()) {
          validDate = joined;
        }
        else if (validDate) {
          break;
        }
      }
      if (validDate) {
        result.isolateDate = validDate;
        break;
      }
    }
  }
  return result;
}

function popAuthorSubtypeFromQualifiers(qualifier, names = ['note', 'subtype', 'group', 'genotype']) {
  let result = {};
  for (const name of names) {
    result = Object.assign(result, popFromQualifier(qualifier, name, 'authorSubtype'));
  }
  if ('authorSubtype' in result) {
    result.authorSubtype = result.authorSubtype.replace(/^.*(subtype|group|genotype):\s+/, '');
  }
  return result;
}


function popPtAliasFromComment(comment) {
  const result = {};
  if (comment) {
    const match = /Patient code (\S+)/.exec(comment.textContent);
    if (match) {
      result.ptAlias = match[1];
    }
  }
  return result;
}


function normalizeSpecies(species) {
  if (!species) {
    return {};
  }
  if (/HIV-?1|HUMAN IMMUNODEFICIENCY VIRUS (?:TYPE )?1/i.test(species)) {
    return {species: 'HIV1'};
  }
  else if (/HIV-?2|HUMAN IMMUNODEFICIENCY VIRUS (?:TYPE )?2/i.test(species)) {
    return {species: 'HIV2'};
  }
  else {
    return {};
  }
}


const HUMAN_HOST_PATTERN = /\b(woman|man|human|homo sapiens)\b/i;

function popHostFromIsolationSource(qualifier) {
  const qualifierName = qualifier.querySelector('GBQualifier_name');
  const result = {};
  if (qualifierName.textContent === 'isolation_source') {
    const value = qualifier
      .querySelector('GBQualifier_value').textContent;
    if (HUMAN_HOST_PATTERN.test(value)) {
      result.host = HUMAN_HOST_PATTERN.exec(value)[1];
    }
  }
  return result;
}

function getGenbankRequest(ids) {
  const request = new Request(
    'https://eutils.ncbi.nlm.nih.gov/entrez/eutils/efetch.fcgi',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: `db=nuccore&id=${ids.join(',')}&retmode=xml&api_key=${NCBI_APIKEY}`
    }
  );
  return request;
}


export async function fetchGenBankFeatureSample(id) {
  const request = getGenbankRequest([id]);
  const response = await fetch(request);
  const body = await response.text();
  const doc = new DOMParser().parseFromString(body, 'application/xml');
  const result = {};
  for (const partial of doc.querySelectorAll('GBSet > GBSeq')) {
    for (const feature of partial.querySelectorAll('GBFeature')) {
      const featureKey = feature.querySelector('GBFeature_key');
      if (featureKey.textContent === 'source') {
        for (const qualifier of feature.querySelectorAll('GBQualifier')) {
          const qualifierName = qualifier.querySelector(
            'GBQualifier_name').textContent;
          let qualifierValue = qualifier.querySelector(
            'GBQualifier_value');
          if (qualifierValue !== null) {
            qualifierValue = qualifierValue.textContent;
          } else {
            qualifierValue = '';
          }
          result[qualifierName] = qualifierValue;
        }
      }
    }
  }
  return result;
}


export async function fetchGenbankGIID(gb_id) {
  const request = getGenbankRequest([gb_id]);
  const response = await fetch(request);
  const body = await response.text();
  const doc = new DOMParser().parseFromString(body, 'application/xml');
  for (const partial of doc.querySelectorAll('GBSet > GBSeq')) {
    for (const gid of partial.querySelectorAll('GBSeqid')) {
      const text = gid.textContent;
      if (text.startsWith('gi|')) {
        return text.split('|')[1];
      }
    }
  }
}

export async function fetchGenbankSequences(ids, giKey = true) {
  const request = getGenbankRequest(ids);
  const response = await fetch(request);
  const body = await response.text();
  const doc = new DOMParser().parseFromString(body, 'application/xml');
  const all = {};
  for (const partial of doc.querySelectorAll('GBSet > GBSeq')) {
    const seq = {
      accession: partial.querySelector('GBSeq_primary-accession').textContent,
      definition: partial.querySelector('GBSeq_definition').textContent,
      sequence: partial.querySelector('GBSeq_sequence').textContent.toUpperCase()
    };
    const comment = partial.querySelector('GBSeq_comment');
    Object.assign(seq, popPtAliasFromComment(comment));
    for (const feature of partial.querySelectorAll('GBFeature')) {
      const featureKey = feature.querySelector('GBFeature_key');
      if (featureKey.textContent === 'source') {
        for (const qualifier of feature.querySelectorAll('GBQualifier')) {
          Object.assign(seq,
            popFromQualifier(qualifier, 'isolate', 'isolateName'),
            popFromQualifier(qualifier, 'strain', 'isolateName'),
            'isolateDate' in seq ? {} : popIsolateDateFromQualifiers(qualifier),
            popFromQualifier(qualifier, 'collection_date', 'isolateDate'),
            popFromQualifier(qualifier, 'organism', 'species'),
            popHostFromIsolationSource(qualifier),
            popFromQualifier(qualifier, 'host', 'host'),
            popFromQualifier(qualifier, 'country', 'region'),
            popAuthorSubtypeFromQualifiers(qualifier),
            popFromQualifier(qualifier, 'clone', 'cloneName'),
            popIsolationSourceFromMolType(qualifier),
            popIsolationSource(qualifier)
          );
        }
      }
    }
    if (giKey) {
      for (const elem of partial.querySelectorAll('GBSeq_other-seqids > GBSeqid')) {
        const value = elem.textContent;
        if (value.startsWith('gi|')) {
          all[value.slice(3)] = seq;
          break;
        }
      }
    }
    else {
      all[seq.accession] = seq;
    }
  }
  return all;
}


const DEFINITION_PATTERNS = {
  existingPatientId: /ptid:\s*(\d+)/i,
  isolateName: /(?:isolate[ _-]?Name|isolate|strain)[: ]\s*([\w.-]+)/i,
  isolateDate: /isolate[ _-]?Date[: ]\s*([\w.-]+)/i,
  ptAlias: /ptAlias[: ]\s*([\w.-]+)/i,
  species: /(?:organism|species):\s*([\w.-]+)/i,
  host: /host:\s*([\w.-]+)/i,
  region: /(?:country|region):\s*([\w.-]+)/i,
  authorSubtype: /(?:subtype|genotype|group|author[ _-]?Subtype)[: ]\s*([\w.-]+)/i,
  cloneName: /(?:clone|clone[ _-]?Name)[: ]\s*([\w.-]+)/i
};


function parseFastaDefinition(definition) {
  const seq = {};
  for (const [key, pattern] of Object.entries(DEFINITION_PATTERNS)) {
    if (pattern.test(definition)) {
      const match = pattern.exec(definition);
      seq[key] = match[1];
    }
  }
  if (!('species' in seq)) {
    Object.assign(seq, normalizeSpecies(definition));
  }
  // const accessionMatch = /^([A-Z]+\d+)$/.exec(definition);
  // if (accessionMatch) {
  //   seq.accession = accessionMatch[1];
  // }
  return seq;
}


function parseInputFastaFile(fileContent, noGBIdxStart, defaults) {
  const sequences = parseFasta(fileContent, 'FASTA');
  const result = [];
  const zeros = '0'.repeat(('' + sequences.length).length);
  for (const idx in sequences) {
    const {header: definition, sequence} = sequences[idx];
    let gbIdx = '' + (parseInt(idx) + noGBIdxStart);
    gbIdx = zeros.substring(0, zeros.length - gbIdx.length) + gbIdx;
    const seq = {
      ...defaults,
      accession: `noGB${gbIdx}`,
      definition,
      sequence
    };
    Object.assign(seq, parseFastaDefinition(definition));

    if (seq['accession'].startsWith('noGB')) {
      const isolateName = /^([A-Z]+\d+)$/.exec(definition);
      if (isolateName) {
        seq['isolateName'] = isolateName[1];
      }
    }

    if (!('isolateName' in seq)) {
      seq['isolateName'] = definition;
    }

    // Hosts are always human, 2020-05-12, decision was made in a meeting.
    seq.host = 'HUMAN';

    result.push(seq);
  }
  return result;
}


export function exportSequenceFile(sequences, incomplete = false, fileName = 'sequence-datafile.txt') {
  let csvHeaders = CSV_HEADERS;
  if (incomplete) {
    csvHeaders = MUTS_CSV_HEADERS;
  }
  return dumpTSVFile(sequences, csvHeaders, fileName);
}


export async function parseInputSequenceFile(file, noGBIdxStart, defaults = null, incomplete = false) {
  const fileContent = await readFile(file);
  const fileType = guessFileType(file, fileContent);
  defaults = defaults || {};
  let seqs = [];
  let csvHeaders = CSV_HEADERS;
  if (incomplete) {
    csvHeaders = MUTS_CSV_HEADERS;
  }
  if (fileType === 'fasta') {
    if (incomplete) {
      alert('FASTA file is not allowed for incomplete sequences.');
      return [];
    }
    seqs = parseInputFastaFile(fileContent, noGBIdxStart, defaults);
  }
  else if (fileType === 'tsv') {
    seqs = parseCSVFile(fileContent, csvHeaders, '\t', true, defaults);
  }
  else if (fileType === 'csv') {
    seqs = parseCSVFile(fileContent, csvHeaders, ',', true, defaults);
  }
  return seqs;
}



// WEBPACK FOOTER //
// ./src/utils/sequence-helper.js